/**
 * Admin candidate-review API client (Plan 03-07). Consumes the Plan 03-03 admin
 * endpoints via the shared admin `apiFetch` (admin JWT + 401→/login) — NOT the
 * candidate surface's candidateFetch.
 *
 * Types mirror backend/app/schemas/admin_candidates.py EXACTLY (BUG-017).
 */
import { apiFetch } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { ApiError } from "@/types/api";
import type { ApiErrorBody } from "@/types/api";

// ---- Response shapes (match backend/app/schemas/admin_candidates.py) ----

export type StatusFilter =
  | "all"
  | "in_progress"
  | "submitted"
  | "advanced_to_stage_2"
  | "finalist"
  | "rejected"
  | "default";

export interface CandidateRow {
  id: number;
  rank: number;
  first_name: string | null;
  primer_apellido: string | null;
  segundo_apellido: string | null;
  email: string | null;
  stage_1_score: number | null;
  salary_expectation: number | null;
  salary_currency: "CRC" | "USD" | null;
  submitted_at: string | null;
  stage_1_status: string;
  stage: CandidateStage; // BUG-090: coarse lifecycle for the Finalista/Contratado Etapa label
  tab_switch_count: number;
  prior_applications_count: number;
  too_fast: boolean;
  too_slow: boolean;
}

export interface CandidateListResponse {
  candidates: CandidateRow[];
  total: number;
  counts_by_status: Record<string, number>;
}

export interface TabSwitchEventRow {
  id: number;
  occurred_at: string;
  question_id: number | null;
  event_type: "blur" | "focus";
  duration_seconds: number | null;
}

export interface QuestionSnapshot {
  text?: string;
  type?: string;
  options?: Record<string, string> | null;
  competency?: string;
  correct_answer?: string | null;
  [k: string]: unknown;
}

export interface CandidateAnswerDetail {
  position_question_id: number;
  question_snapshot: QuestionSnapshot;
  question_type: "multiple_choice" | "short_answer";
  choice_id: string | null;
  short_answer_text: string | null;
  is_correct: boolean | null;
  score_contribution: number;
  elapsed_seconds: number;
}

export interface PriorAppSummary {
  position_id: number;
  position_title: string;
  stage_1_status: string;
  submitted_at: string | null;
}

export interface FairnessFlags {
  too_fast: boolean;
  too_slow: boolean;
}

export interface CandidateDetail {
  id: number;
  position_id: number;
  position_title: string;
  first_name: string | null;
  primer_apellido: string | null;
  segundo_apellido: string | null;
  email: string | null;
  phone: string | null;
  salary_expectation: number | null;
  salary_currency: "CRC" | "USD" | null;
  earliest_start_date: string | null;
  stage_1_status: string;
  stage_1_score: number | null;
  reference_number: string | null;
  started_at: string | null;
  submitted_at: string | null;
  consent_accepted_at: string;
  consent_version_id: number;
  consent_ip_address: string;
  mc_answers: CandidateAnswerDetail[];
  short_answer_responses: CandidateAnswerDetail[];
  tab_switch_count: number;
  tab_switch_timeline: TabSwitchEventRow[];
  prior_applications_count: number;
  viewer_scoped_prior_applications: PriorAppSummary[];
  per_competency_scores: Record<string, number>;
  total_elapsed_seconds: number;
  fairness_flags: FairnessFlags;
  // Phase 4 Stage-2 review enrichment (Plan 04-07, backend Eva option A)
  stage: CandidateStage;
  shortlist_tag: ShortlistTag | null;
  salary_expectation_stage2: number | null;
  has_video: boolean;
  stage2_rubric: Stage2RubricDetail | null;
  rubric_config: Record<string, unknown>;
  hiring_status_notes: string | null; // OPS-04 — editable once finalist; null pre-finalist
}

export type CandidateStage =
  | "applied"
  | "stage1_done"
  | "stage2_done"
  | "finalist"
  | "hired"
  | "rejected";

export type ShortlistTag = "shortlist" | "maybe" | "no";

/** Saved Stage-2 rubric (prefill) — matches backend Stage2RubricDetail EXACTLY (BUG-017). */
export interface Stage2RubricDetail {
  language_fluency: number | null;
  confidence: number | null;
  professional_impression: number | null;
  relevance_of_example: number | null;
  red_flags: string[];
  notes: string | null;
  composite_score: number | null;
}

export interface CandidateActionResponse {
  candidate_id: number;
  stage_1_status: string;
  updated_at: string;
}

export interface Stage2InviteCopy {
  subject: string;
  body_md: string;
  body_text: string;
  video_link: string;
}

// ---- Endpoints (Plan 03-03 backend) ----

export async function listCandidatesForPosition(
  positionId: number,
  status: StatusFilter = "default",
): Promise<CandidateListResponse> {
  return apiFetch<CandidateListResponse>(
    `/api/admin/positions/${positionId}/candidates?status=${encodeURIComponent(status)}`,
  );
}

export async function getCandidateDetail(id: number): Promise<CandidateDetail> {
  return apiFetch<CandidateDetail>(`/api/admin/candidates/${id}`);
}

export async function advanceCandidate(
  id: number,
  note?: string,
): Promise<CandidateActionResponse> {
  return apiFetch<CandidateActionResponse>(
    `/api/admin/candidates/${id}/advance`,
    { method: "POST", body: JSON.stringify({ note: note ?? null }) },
  );
}

export async function rejectCandidate(
  id: number,
  reason?: string,
): Promise<CandidateActionResponse> {
  return apiFetch<CandidateActionResponse>(
    `/api/admin/candidates/${id}/reject`,
    { method: "POST", body: JSON.stringify({ reason: reason ?? null }) },
  );
}

export async function unrejectCandidate(
  id: number,
): Promise<CandidateActionResponse> {
  return apiFetch<CandidateActionResponse>(
    `/api/admin/candidates/${id}/unreject`,
    { method: "POST" },
  );
}

export async function getStage2InviteCopy(
  id: number,
  language: "auto" | "es" | "en" | "de" = "auto",
): Promise<Stage2InviteCopy> {
  return apiFetch<Stage2InviteCopy>(
    `/api/admin/candidates/${id}/stage-2-invite-copy?language=${encodeURIComponent(language)}`,
  );
}

// ======================================================================== //
// Phase 4 Stage-2 decision-support endpoints (Plan 04-04 / 04-05 backend)   //
// ======================================================================== //

export interface VideoTokenResponse {
  vtoken: string;
}

/** PUT rubric body — the 4 numeric dims 0-5 (null = unscored); matches RubricSaveRequest. */
export interface RubricSavePayload {
  language_fluency: number | null;
  confidence: number | null;
  professional_impression: number | null;
  relevance_of_example: number | null;
  red_flags: string[];
  notes: string | null;
}

export interface RubricSaveResponse {
  candidate_id: number;
  composite_score: number | null;
  language_fluency: number | null;
  confidence: number | null;
  professional_impression: number | null;
  relevance_of_example: number | null;
  red_flags: string[];
  notes: string | null;
}

export interface ShortlistTagResponse {
  candidate_id: number;
  shortlist_tag: ShortlistTag | null;
}

/** GET positions/{id}/shortlist row — matches backend ShortlistRow EXACTLY (BUG-017). */
export interface ShortlistRow {
  id: number;
  rank: number;
  stage: CandidateStage;
  first_name: string | null;
  primer_apellido: string | null;
  segundo_apellido: string | null;
  composite_score: number | null;
  language_fluency: number | null;
  confidence: number | null;
  professional_impression: number | null;
  relevance_of_example: number | null;
  red_flags: string[];
  salary_expectation: number | null;
  salary_expectation_stage2: number | null;
  salary_currency: string | null;
}

export interface StageActionResponse {
  candidate_id: number;
  stage: CandidateStage;
}

/** GET positions/{id}/summary — matches backend SummaryResponse (RPT-03 tiles). */
export interface SummaryResponse {
  position_id: number;
  stage_counts: Record<string, number>;
  avg_score: number | null;
  avg_salary: number | null;
  video_receipt_rate: number | null;
}

/** Mint a 5-min scoped vtoken for inline playback (FIN-01). */
export async function mintVideoToken(id: number): Promise<VideoTokenResponse> {
  return apiFetch<VideoTokenResponse>(
    `/api/admin/candidates/${id}/video-token`,
  );
}

/** Inline <video src> URL — auth is the vtoken query param (native video can't send Bearer). */
export function videoStreamUrl(id: number, vtoken: string): string {
  return `/api/admin/candidates/${id}/video?vtoken=${encodeURIComponent(vtoken)}`;
}

export async function saveRubric(
  id: number,
  payload: RubricSavePayload,
): Promise<RubricSaveResponse> {
  return apiFetch<RubricSaveResponse>(`/api/admin/candidates/${id}/rubric`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function setShortlistTag(
  id: number,
  tag: ShortlistTag,
): Promise<ShortlistTagResponse> {
  return apiFetch<ShortlistTagResponse>(
    `/api/admin/candidates/${id}/shortlist-tag`,
    { method: "POST", body: JSON.stringify({ tag }) },
  );
}

export async function getShortlist(positionId: number): Promise<ShortlistRow[]> {
  return apiFetch<ShortlistRow[]>(
    `/api/admin/positions/${positionId}/shortlist`,
  );
}

export async function promoteFinalist(id: number): Promise<StageActionResponse> {
  return apiFetch<StageActionResponse>(
    `/api/admin/candidates/${id}/promote-finalist`,
    { method: "POST" },
  );
}

export async function demoteFinalist(id: number): Promise<StageActionResponse> {
  return apiFetch<StageActionResponse>(
    `/api/admin/candidates/${id}/demote-finalist`,
    { method: "POST" },
  );
}

/** OPS-04: finalist → hired (terminal). HR/super-admin only (backend 403s otherwise). */
export async function setHired(id: number): Promise<StageActionResponse> {
  return apiFetch<StageActionResponse>(
    `/api/admin/candidates/${id}/set-hired`,
    { method: "POST" },
  );
}

/** OPS-04: update the HR-editable hiring_status_notes on the finalist's FinalistDecision. */
export async function updateHiringStatusNotes(
  id: number,
  notes: string,
): Promise<{ candidate_id: number; hiring_status_notes: string | null }> {
  return apiFetch<{ candidate_id: number; hiring_status_notes: string | null }>(
    `/api/admin/candidates/${id}/hiring-status-notes`,
    { method: "PUT", body: JSON.stringify({ notes }) },
  );
}

export async function getPositionSummary(
  positionId: number,
): Promise<SummaryResponse> {
  return apiFetch<SummaryResponse>(
    `/api/admin/positions/${positionId}/summary`,
  );
}

/**
 * Authed file download (PDF/CSV). Native <a download> can't send the admin JWT, so we
 * fetch the file as a blob with the Bearer header, read the BUG-006 filename from
 * Content-Disposition, and trigger the browser download via an object URL.
 */
async function downloadAuthedFile(
  path: string,
  fallbackName: string,
): Promise<void> {
  const token = useAuthStore.getState().token;
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body: ApiErrorBody = await res
      .json()
      .catch(() => ({ detail: "api_error" }));
    throw new ApiError(res.status, body, res.headers.get("x-request-id") ?? undefined);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const name = m?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadCandidatePdf(id: number): Promise<void> {
  return downloadAuthedFile(
    `/api/admin/candidates/${id}/pdf`,
    `candidate_${id}.pdf`,
  );
}

export async function downloadPositionCsv(positionId: number): Promise<void> {
  return downloadAuthedFile(
    `/api/admin/positions/${positionId}/csv`,
    `position_${positionId}.csv`,
  );
}
